<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header
      :header-title="isEditing ? $t('BUSINESS_DATA.ITEMS.EDIT') : $t('BUSINESS_DATA.ITEMS.ADD_NEW')"
    />
    <form
      class="flex flex-col modal-content"
      @submit.prevent="onSubmit"
    >
      <div class="space-y-5">
          <div class="space-y-2">
            <woot-input
              v-model="formData.title"
              :label="$t('BUSINESS_DATA.ITEMS.TITLE')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.ITEMS.TITLE_PLACEHOLDER')"
              :error="v$.formData.title.$error ? $t('BUSINESS_DATA.ITEMS.TITLE_ERROR') : ''"
              :class="{ error: v$.formData.title.$error }"
              maxlength="100"
              @blur="v$.formData.title.$touch"
            />
            <div v-if="!v$.formData.title.$error" class="flex justify-end text-xs">
              <span :class="formData.title.length > 80 ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
                {{ formData.title.length }}/100
              </span>
            </div>
          </div>

          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.ITEMS.DESCRIPTION')"
              :required="true"
              :has-error="v$.formData.description.$error"
            />
            <TextArea
              v-model="formData.description"
              @update:modelValue="v$.formData.description.$touch()"
              name="business-item-description"
              :placeholder="$t('BUSINESS_DATA.ITEMS.DESCRIPTION_PLACEHOLDER')"
              :max-length="2000"
              show-character-count
              :message="v$.formData.description.$error ? $t('BUSINESS_DATA.ITEMS.DESCRIPTION_ERROR') : ''"
              :message-type="v$.formData.description.$error ? 'error' : 'info'"
              auto-height
              :resize="true"
              :min-height="'100px'"
              :disabled="isSaving"
              @blur="v$.formData.description.$touch"
            />
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.ITEMS.CATEGORY') }}
            </label>
            <CategorySelector
              v-model="formData.business_category_id"
              :categories="businessCategories"
              category-type="data"
              :placeholder="$t('BUSINESS_DATA.ITEMS.CATEGORY_PLACEHOLDER')"
              @category-created="onCategoryCreated"
              @category-updated="onCategoryUpdated"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BUSINESS_DATA.ITEMS.CATEGORY_HELP') }}
            </p>
          </div>

          <div class="space-y-2">
            <div class="flex items-center gap-3">
              <woot-switch
                :model-value="formData.is_active"
                @input="formData.is_active = $event"
              />
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('BUSINESS_DATA.ITEMS.ACTIVE') }}
              </span>
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BUSINESS_DATA.ITEMS.ACTIVE_HELP') }}
            </p>
          </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button
          type="button"
          variant="smooth"
          color-scheme="ghost"
          @click="onClose"
        >
          {{ $t('COMMON.CANCEL') }}
        </woot-button>

        <woot-button
          type="submit"
          variant="smooth"
          color-scheme="success"
          :is-loading="isSaving"
          :disabled="!isFormValid"
        >
          {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import CategorySelector from './CategorySelector.vue';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'DataItemModal',
  components: { CategorySelector, FormLabel, TextArea },
  setup() {
    return { v$: useVuelidate() };
  },
  
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    dataItem: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    businessCategories: {
      type: Array,
      default: () => [],
    },
  },
  
  emits: ['close', 'saved', 'category-created'],
  
  data() {
    return {
      isSaving: false,
      formData: {
        title: '',
        description: '',
        business_category_id: null,
        is_active: true,
      },
    };
  },
  
  computed: {
    isEditing() {
      return !!this.dataItem;
    },
    
    isFormValid() {
      return (
        this.formData.title.trim() &&
        this.formData.description.trim() &&
        this.formData.title.length <= 100 &&
        this.formData.description.length <= 2000
      );
    },
    
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  validations: {
    formData: {
      title: { required, maxLength: maxLength(100) },
      description: { required, maxLength: maxLength(2000) },
    },
  },
  
  watch: {
    dataItem: {
      immediate: true,
      handler(newItem) {
        if (newItem) {
          this.formData = {
            title: newItem.title || '',
            description: newItem.description || '',
            business_category_id: newItem.business_category_id || null,
            is_active: newItem.is_active !== undefined ? newItem.is_active : true,
          };
        } else {
          this.formData = {
            title: '',
            description: '',
            business_category_id: null,
            is_active: true,
          };
        }
      },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
      }
    },
  },
  
  methods: {
    resetForm() {
      this.formData = {
        title: '',
        description: '',
        business_category_id: null,
        is_active: true,
      };
      this.v$.$reset();
    },
    
    onCategoryCreated(newCategory) {
      // Auto select newly created category
      this.formData.business_category_id = newCategory.id;
      this.$emit('category-created', newCategory);
    },

    onCategoryUpdated() {
      this.$emit('category-updated');
    },
    
    async onSubmit() {
      if (!this.isFormValid || this.isSaving) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          title: (this.formData.title || '').trim(),
          description: (this.formData.description || '').trim(),
          business_category_id: this.formData.business_category_id,
          is_active: this.formData.is_active,
        };
        
        if (this.isEditing) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/business_data_items/${this.dataItem.id}`,
            payload
          );
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/business_data_items`,
            payload
          );
        }
        
        this.$emit('saved');
        this.resetForm();
      } catch (error) {
        console.error('Error saving data item:', error);
        console.error('Error response:', error?.response?.data);
        
        let errorMessage = this.$t('BUSINESS_DATA.ERROR.SAVE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isSaving = false;
      }
    },
    
    onClose() {
      this.resetForm();
      this.$emit('close');
    },
  },
};
</script>
